import { defineStore } from 'pinia'
import * as challengeApi from '@/network/api/challenge.api.js'
import * as healthcheckApi from '@/network/api/healthcheck.api.js'
import { getItemFromSessionStorage, addItemToSessionStorage, addItemToLocalStorage, getItemFromLocalStorage } from '@/assets/scripts/windowStorage'
import { USER_SESSION } from '@/assets/constants/core'
import { useSessionStore } from '@/store/session'

export const useProjectBaseStore = defineStore('projectBaseStore', {
    state: () => ({
        deviceInUse: 'Desktop',
        mobileApp: false,
        isCordovaApp: false,
        showAlertRememberMeModal: false,
        appThemeClientType: 'particular',
        themeModeId: 'light',
        themeMode: 'light',
        statusBarStyle: 'lightContent',
        preventMobileFeatureBack: false,
        preventMobileSideModalBack: false,
        appScroll: 0,
        sideModalScroll: 0,
        footerIsBeingShowed: false,
        applyTransparencyToApp: false,
        keyboardHeight: 0,
        isTokenChallengeModalVisible: false,
        tokenChallengeData: null,
        routeParams: null,
        requests: [],
        versionStatus: null,
        newVersionData: null,
        checkingForcedUpdate: false
    }),
    getters: {
        getDeviceInUse: (state) => state.deviceInUse,
        getMobileAppState: (state) => state.mobileApp,
        getIsCordovaApp: (state) => state.isCordovaApp,
        getShowAlertRememberMeModalState: (state) => state.showAlertRememberMeModal,
        getStatusBarStyle: (state) => state.statusBarStyle,
        getAppTheme: (state) => `${state.appThemeClientType}-${state.themeMode}`,
        getClientType: (state) => state.appThemeClientType,
        getAppThemeModeId: (state) => state.themeModeId,
        getAppThemeMode: (state) => state.themeMode,
        getPreventMobileFeatureBackState: (state) => state.preventMobileFeatureBack,
        getPreventMobileSideModalBackState: (state) => state.preventMobileSideModalBack,
        getAppScroll: (state) => state.appScroll,
        getSideModalScroll: (state) => state.sideModalScroll,
        getFooterState: (state) => state.footerIsBeingShowed,
        getKeyboardHeight: (state) => state.keyboardHeight,
        getApplyTransparencyToApp: (state) => state.applyTransparencyToApp,
        getRouteParams: (state) => state.routeParams,
        getShowTokenModalDisplayState: (state) => state.isTokenChallengeModalVisible,
        getTokenModalInfo: (state) => state.tokenChallengeData,
        getNewVersionData: (state) => state.newVersionData
    },
    actions: {
        preLoadStoreData(payload) {
            const sessionStore = useSessionStore()

            let rememberMeEntityInfo
            const key = 'bai-remember-me-entity-info'

            if (window.cordova) {
                const moveToSharedPreferencesIfNeeded = () => {
                    rememberMeEntityInfo = JSON.parse(window.localStorage.getItem(key))
                    if (rememberMeEntityInfo) {
                        const sharedPreferences = window.plugins.SharedPreferences.getInstance()
                        sharedPreferences.put(key, JSON.stringify(rememberMeEntityInfo), () => {
                            window.localStorage.removeItem(key)
                        })
                    }
                }
                if (payload) {
                    rememberMeEntityInfo = JSON.parse(payload)
                } else {
                    moveToSharedPreferencesIfNeeded()
                }
            } else {
                rememberMeEntityInfo = JSON.parse(window.localStorage.getItem(key))
            }

            const sessionInfo = getItemFromSessionStorage(USER_SESSION)
            const baiV4SavedTheme = getItemFromLocalStorage('bai-cv-theme-mode')

            sessionStore.currentAppThemeClientType = this.appThemeClientType

            if (rememberMeEntityInfo) {
                sessionStore.rememberMeEntityInfo = rememberMeEntityInfo
            }
            if (baiV4SavedTheme) {
                this.themeMode = baiV4SavedTheme.theme
                this.themeModeId = baiV4SavedTheme.themeModeId
            }
            if (sessionInfo) {
                sessionStore.allUserInfo = sessionInfo
                sessionStore.userSession = sessionInfo.session

                const remainingSeconds = sessionInfo.session.remainingSecondsForSessionExpire
                const currentTime = new Date().getTime()
                const expiryDate = new Date()
                expiryDate.setTime(currentTime + remainingSeconds * 1000)
                sessionStore.localExpiryDate = expiryDate.getTime()
                sessionStore.localExpirySpan = (sessionStore.localExpiryDate - currentTime) / 1000
            }
        },
        saveRouteForRedirectAfterRefresh(payload) {
            addItemToSessionStorage('bai-v4-aux-redirect-link-after-refresh', payload)
        },
        updateDeviceInUse(payload) {
            this.deviceInUse = payload
        },
        updateIsMobileApp(payload) {
            this.mobileApp = payload
        },
        updateIsCordovaApp(payload) {
            this.isCordovaApp = payload
        },
        updateShowAlertRememberMeModal(payload) {
            this.showAlertRememberMeModal = payload
        },
        async checkForUpdates(payload) {
            const res = await healthcheckApi.checkForUpdates(payload)
            if (res) this.versionStatus = res.data.channelApplicationVersionStatus
            return res
        },
        updateNewVersionData(payload) {
            this.newVersionData = payload
        },
        /* Version deprecated mid-session (MDW error application_version_deprecated): show the update screen in mandatory mode */
        async showForcedUpdate() {
            if (this.newVersionData?.deprecated || this.checkingForcedUpdate) return

            if (!this.versionStatus) {
                this.checkingForcedUpdate = true
                try {
                    await this.checkForUpdates({ serviceBrand: this.appThemeClientType === 'company' ? 'CORPORATE' : 'RETAIL' })
                } catch (error) {
                    console.log(error)
                } finally {
                    this.checkingForcedUpdate = false
                }
            }

            this.newVersionData = { ...this.versionStatus, deprecated: true }
        },
        updateStatusBarStyle(payload) {
            this.statusBarStyle = payload
        },
        updateAppThemeClientType(payload) {
            const sessionStore = useSessionStore()
            this.appThemeClientType = payload
            sessionStore.currentAppThemeClientType = payload
        },
        async updateAppThemeMode(payload) {
            let theme = payload
            try {
                if (payload === 'system') {
                    if (window.cordova?.platformId === 'android') {
                        await window.cordova.plugins.ThemeDetection.isAvailable()
                        await window.cordova.plugins.ThemeDetection.isDarkModeEnabled($event => {
                            theme = $event.value ? 'dark' : 'light'
                        })
                    } else {
                        theme = window.matchMedia('(prefers-color-scheme:dark)').matches ? 'dark' : 'light'
                    }
                }
            } catch {
                theme = 'light'
            } finally {
                this.themeMode = theme
                this.statusBarStyle = 'lightContent'
                if (window.StatusBar) window.StatusBar.styleLightContent()
                this.themeModeId = payload
                addItemToLocalStorage('bai-cv-theme-mode', { themeModeId: this.themeModeId, theme: this.themeMode })
            }
        },
        updatePreventMobileFeatureBack(payload) {
            this.preventMobileFeatureBack = payload
        },
        updatePreventMobileSideModalBack(payload) {
            this.preventMobileSideModalBack = payload
        },
        updateAppScroll(payload) {
            this.appScroll = payload
        },
        updateSideModalScroll(payload) {
            this.sideModalScroll = payload
        },
        updateFooterShowState(payload) {
            this.footerIsBeingShowed = payload
        },
        updateKeyboardHeight(payload) {
            this.keyboardHeight = payload
        },
        updateApplyTransparencyToApp(payload) {
            this.applyTransparencyToApp = payload
        },
        addToRequestsList(payload) {
            setTimeout(() => {
                this.requests.push(payload)
            })
        },
        cancelAllPendingRequests() {
            this.requests.forEach(element => {
                element.source.cancel()
            })
            this.requests = []
        },
        saveRouteParams(payload) {
            this.routeParams = payload
        },
        removeRouteParams() {
            this.routeParams = null
        },
        showTokenChallengeModal(payload) {
            this.isTokenChallengeModalVisible = true
            this.tokenChallengeData = payload
        },
        hideTokenChallengeModal() {
            this.isTokenChallengeModalVisible = false
            this.tokenChallengeData = null
        },
        async submitChallenge(payload) {
            return await challengeApi.submitChallenge(payload)
        },
        async requestNewChallenge(payload) {
            return await challengeApi.requestNewChallenge(payload)
        }
    }
})
